import styled from '@emotion/styled'
import { WebLink } from '@news-mono/web-common'
import { linearGradient } from 'polished'
import { TheWestLiveLogo } from '../../logos/TheWestLiveLogo/TheWestLiveLogo'
import { SectionHeader } from '../../section-header/SectionHeader/SectionHeader'
import {
    StyledChevronRight,
    StyledHeadingLink,
    StyledSectionHeader,
    StyledWebLink,
} from '../../section-header/SectionHeader/SectionHeader.styled'
import { breakpoint } from '../../__styling'
import {
    ContainerWidth,
    metrics,
    ThemeMargins,
} from '../../__styling/settings/metrics'
import { calcRem } from '../../__styling/style-functions/calc-rem'

type Gutters = boolean | keyof ThemeMargins

export interface StyledThorProps {
    hasBackgroundFill?: boolean
    /** If true or undefined, will default to the outer margin of the theme */
    horizontalGutters?: Gutters
    /** If true or undefined, will default to the outer margin of the theme */
    verticalGutters?: Gutters
    verticalSpacing?: keyof ThemeMargins
    containerWidth?: ContainerWidth
    useStylingForHiddenPodcast?: boolean
}

interface ThemedMetrics {
    margins: ThemeMargins
}

const getGutterSize = (
    themedMetrics: ThemedMetrics,
    gutter?: Gutters,
): string | number => {
    return gutter === false
        ? 0
        : calcRem(
              typeof gutter === 'string'
                  ? themedMetrics.margins[gutter]
                  : themedMetrics.margins.outerMargin,
          )
}

export const StyledThor = styled('div')<StyledThorProps>(
    {
        margin: '0 auto',
        width: '100%',
        display: 'block',
    },
    ({
        theme,
        verticalSpacing,
        horizontalGutters,
        verticalGutters,
        hasBackgroundFill,
        containerWidth,
        useStylingForHiddenPodcast,
    }) => {
        const themedMetrics = metrics[theme.kind]

        return {
            marginBottom: useStylingForHiddenPodcast
                ? theme.margins.xl
                : verticalSpacing &&
                  calcRem(themedMetrics.margins[verticalSpacing]),
            paddingLeft: getGutterSize(themedMetrics, horizontalGutters),
            paddingRight: getGutterSize(themedMetrics, horizontalGutters),
            paddingTop: getGutterSize(themedMetrics, verticalGutters),
            paddingBottom: useStylingForHiddenPodcast
                ? undefined
                : getGutterSize(themedMetrics, verticalGutters),
            background: hasBackgroundFill
                ? theme.colors.background.secondary
                : 'transparent',
            color: theme.colors.text.primary,
            maxWidth: containerWidth,
        }
    },
)

// DPT-3630 olympics customisations
export const StyledFeatureContainer = styled('div')(({ theme }) => ({
    alignItems: 'flex-start',
    background: theme.colors.background.secondary,
    color: theme.colors.text.primary,
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    padding: calcRem(theme.margins.sm, theme.margins.md),
    [breakpoint('sm')]: {
        background: theme.colors.background.secondary,
        flexDirection: 'row',
        alignItems: 'center',
    },
}))
export const StyledBackgroundWrap = styled('div')(({ theme }) => ({
    background: theme.colors.background.primary,
}))

// DTEC-788: Federal Election
export const StyledGradientWrap = styled('div')(({ theme }) => ({
    ...linearGradient({
        colorStops: ['#1E04E2', '#B03D28'],
        fallback: 'transparent',
        toDirection: '90deg',
    }),
    padding: calcRem(4),
    boxShadow: '0px 8px 20px 0px rgb(0 0 0 / 47%)',
    marginBottom: theme.margins.xl,
    overflow: 'hidden',

    [`& ${StyledHeadingLink}`]: {
        color: theme.colors.brand,
    },

    [`& ${StyledChevronRight}`]: {
        fill: theme.colors.brand,
    },
}))

export const StyledGradientSectionTitle = styled('h2')(({ theme }) => ({
    fontSize: calcRem(32),
    fontWeight: 700,
    margin: 0,
    fontFamily: theme.fonts.sansSerifCond,
    textTransform: 'uppercase',
}))

export const StyledFeatureSectionHeader = styled(SectionHeader)(
    ({ theme }) => ({
        borderBottom: `${theme.colors.borders.primary} solid 1px`,
        h2: {
            padding: calcRem(0, 0, 16, 0),
            margin: 0,
        },
        [breakpoint('sm')]: {
            width: 'auto',
            borderBottom: '0px',
            h2: {
                padding: 0,
            },
        },

        [`& ${StyledSectionHeader}::before`]: {
            display: 'none',
        },
    }),
)

// DTEC-850: Federal Election - Banner
export const StyledPollieRaterBannerWrap = styled('div')(({ theme }) => ({
    background: theme.colors.palette.white,
    padding: calcRem(8, 14, 16, 14),
}))

export const StyledPollieRaterBanner = styled('div')(({ theme }) => ({
    ...linearGradient({
        colorStops: ['#1E04E2', '#B03D28'],
        fallback: 'transparent',
        toDirection: '90deg',
    }),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: calcRem(72),
    width: '100%',
    padding: calcRem(16),
    borderRadius: 4,

    [breakpoint('sm')]: {
        flexDirection: 'row',
        justifyContent: 'center',
    },
}))

export const StyledPollieRaterBannerTitle = styled('h3')(({ theme }) => ({
    fontSize: calcRem(24),
    fontWeight: 600,
    lineHeight: calcRem(24),
    margin: calcRem(0, 0, 8, 0),
    fontFamily: theme.fonts.sansSerif,
    color: theme.colors.palette.white,
    textAlign: 'center',
    [breakpoint('sm')]: {
        margin: calcRem(0, 32, 0, 0),
    },
}))

export const StyledPollieRaterBannerButton = styled(StyledWebLink)(
    ({ theme }) => ({
        width: calcRem(144),
        height: calcRem(32),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundColor: theme.colors.palette.white,
        color: theme.colors.brand,
        fontFamily: theme.fonts.sansSerif,
        fontSize: calcRem(13),
        fontWeight: 700,
        textDecoration: 'none',
        textAlign: 'center',
        borderRadius: '2px',

        '&:focus': {
            backgroundColor:
                theme.colors.actions.button.secondary!.background.hover,
            color: theme.colors.actions.button.secondary!.text.hover,
        },
    }),
)

const footerLayoutBreakpoint = 'xxs'

export const StyledTheWestLiveLogo = styled(TheWestLiveLogo)(({ theme }) => ({
    gridArea: 'westlive',
    height: 56,
    width: 161,
    marginBottom: calcRem(theme.margins.sm),
    marginRight: calcRem(theme.margins.sm),

    [breakpoint(footerLayoutBreakpoint)]: {
        justifySelf: 'end',
        margin: 0,
        marginRight: calcRem(theme.margins.xl),
        gridRow: '1 / 3',
    },
}))

export const StyledListenButton = styled(StyledWebLink)(({ theme }) => ({
    backgroundColor: theme.colors.actions.button.secondary!.background.default,
    border: `1px solid ${theme.colors.actions.button.secondary!.border}`,
    color: theme.colors.actions.button.secondary!.text.default,
    display: 'block',
    gridArea: 'button',
    margin: 0,

    '&:not(:first-child)': {
        marginLeft: 0,
    },

    '&:hover': {
        backgroundColor:
            theme.colors.actions.button.secondary!.background.hover,
        color: theme.colors.actions.button.secondary!.text.hover,
    },

    '&:focus': {
        backgroundColor:
            theme.colors.actions.button.secondary!.background.hover,
        color: theme.colors.actions.button.secondary!.text.hover,
    },

    [breakpoint(footerLayoutBreakpoint)]: {
        gridArea: 'text',
        gridRow: 2,
        width: 'auto',
        justifySelf: 'start',
    },
}))

export const StyledTitle = styled(WebLink)(({ theme }) => ({
    textDecoration: 'none',
    color: theme.colors.text.primary,

    ['h2']: {
        fontWeight: 'normal',
    },
}))

export const StyledCNBCLogo = styled('img')(({ theme }) => ({
    marginTop: calcRem(theme.margins.sm),
    height: calcRem(24),
    width: calcRem(169),
}))
